
import { computed, defineComponent, PropType, ref } from "vue";

import InfoForm from "@/components/Booking/PersonalData/InfoForm.vue";
import EasyDrop from "@/components/EasyDrop.vue";
import { SearchBookingOverview, SearchHotelRoom } from "@/types/models";
import { FormSearchPerson, LocaleStrings } from "@/types/paramModels";

import { format } from "date-fns";
import * as Locales from "date-fns/locale";

export default defineComponent({
  name: "PersonalDataOverviewView",
  props: {
    bookingOverview: {
      type: Object as PropType<SearchBookingOverview>,
      required: true,
    },
    searchQuery: {
      type: Object,
      required: true,
    },
    pageTexts: {
      type: Object,
      required: true,
    },
    selectedRooms: {
      type: Array as PropType<Array<SearchHotelRoom>>,
      required: true,
    },
    localeStrings: {
      type: Object as PropType<LocaleStrings>,
      required: true,
    },
  },
  components: {
    EasyDrop,
    InfoForm,
  },
  setup(_props) {
    const personIndex = ref(0);
    const isCurrentPersonValid = ref(false);
    const showModal = ref(false);

    const searchBookingOverview = computed(() => _props.bookingOverview);

    const query = computed(() => _props.searchQuery);

    const formAdults = computed(() => {
      const hotelRooms =
        searchBookingOverview.value.bookingSelection.hotelRooms;
      const newAdults = Array<FormSearchPerson>();
      let personTypeIndex = 0;
      for (let roomIndex = 0; roomIndex < hotelRooms.length; roomIndex++) {
        for (
          let personIndex = 0;
          personIndex < hotelRooms[roomIndex].searchRoom.adults.length;
          personIndex++
        ) {
          const newAdult = {
            hotelRoomIndex: roomIndex,
            personIndex: personIndex,
            personTypeIndex: personTypeIndex,
            isAdult: true,
            searchPerson: hotelRooms[roomIndex].searchRoom.adults[personIndex],
          } as FormSearchPerson;
          personTypeIndex++;
          newAdults.push(newAdult);
        }
      }
      return newAdults;
    });

    const formChildren = computed(() => {
      const hotelRooms =
        searchBookingOverview.value.bookingSelection.hotelRooms;
      const newChildren = Array<FormSearchPerson>();
      let personTypeIndex = 0;
      for (let roomIndex = 0; roomIndex < hotelRooms.length; roomIndex++) {
        for (
          let personIndex = 0;
          personIndex < hotelRooms[roomIndex].searchRoom.children.length;
          personIndex++
        ) {
          const newAdult = {
            hotelRoomIndex: roomIndex,
            personIndex: personIndex,
            personTypeIndex: personTypeIndex,
            isAdult: false,
            searchPerson:
              hotelRooms[roomIndex].searchRoom.children[personIndex],
          } as FormSearchPerson;
          personTypeIndex++;
          newChildren.push(newAdult);
        }
      }
      return newChildren;
    });

    const formatDate = (dateString: string) => {
      return format(
        new Date(dateString),
        "dd-MM-yyyy",
        { locale: Locales[_props.localeStrings.name] }
      );
    };

    const setCurrentPersonValid = (isValid: boolean) => {
      isCurrentPersonValid.value = isValid;
    };

    const formPersons = computed(() => {
      return [...formAdults.value, ...formChildren.value];
    });

    const adultsCount = ref(formAdults.value.length);
    const childrenCount = ref(formChildren.value.length);

    const modalPerson = ref<FormSearchPerson>();

    const isAllPersonalDataValid = computed(() => {
      for (const [
        roomIndex,
        room,
      ] of searchBookingOverview.value.bookingSelection.hotelRooms.entries()) {
        for (const [index, person] of room.searchRoom.adults.entries()) {
          if (
            roomIndex === 0 &&
            index === 0 &&
            (!person.firstName ||
              !person.lastName ||
              !person.gender ||
              !person.birthdate ||
              !person.street ||
              !person.postal ||
              !person.city ||
              !person.country ||
              !person.phone ||
              !person.email)
          ) {
            return false;
          } else if (
            index > 0 &&
            (!person.firstName ||
              !person.lastName ||
              !person.gender ||
              !person.birthdate)
          ) {
            return false;
          }
        }
        for (const person of room.searchRoom.children) {
          if (
            !person.firstName ||
            !person.lastName ||
            !person.gender ||
            !person.birthdate
          ) {
            return false;
          }
        }
      }
      return true;
    });

    const openEditModal = (person: FormSearchPerson) => {
      modalPerson.value = person;
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const fixBody = () => {
      document.body.classList.add("modal-active");
    };

    const unfixBody = () => {
      document.body.classList.remove("modal-active");
    };

    return {
      query,
      modalPerson,
      isCurrentPersonValid,
      personIndex,
      adultsCount,
      childrenCount,
      formPersons,
      searchBookingOverview,
      showModal,
      setCurrentPersonValid,
      openEditModal,
      closeModal,
      fixBody,
      unfixBody,
      formatDate,
      isAllPersonalDataValid,
    };
  },
});
